import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Centers data (already cached in centersListSlice)
  selectedCenter: null,
  
  // Categories data
  categories: {},  // { centerId: [categories] }
  categoriesLoading: false,
  categoriesError: null,
  
  // Classes data
  classes: {},  // { `${centerId}_${categoryId}`: [classes] }
  classesLoading: false,
  classesError: null,
  
  // Selected items
  selectedCategory: null,
  selectedClass: null,
  
  // Student enrollment data
  students: [],  // All students for the user
  selectedStudentIds: [],  // IDs of students selected for enrollment
  enrollmentData: null,  // { classId, students, totalFee }
  
  // Country filter
  country: 'USA',
};

const classSlice = createSlice({
  name: 'class',
  initialState,
  reducers: {
    // Set selected center
    setSelectedCenter: (state, action) => {
      state.selectedCenter = action.payload;
    },

    // Set categories for a center
    setCategories: (state, action) => {
      const { centerId, categories } = action.payload;
      state.categories[centerId] = categories;
      state.categoriesError = null;
    },

    // Set categories loading state
    setCategoriesLoading: (state, action) => {
      state.categoriesLoading = action.payload;
    },

    // Set categories error
    setCategoriesError: (state, action) => {
      state.categoriesError = action.payload;
    },

    // Set classes for a center and category
    setClasses: (state, action) => {
      const { centerId, categoryId, classes } = action.payload;
      const key = `${centerId}_${categoryId}`;
      state.classes[key] = classes;
      state.classesError = null;
    },

    // Set classes loading state
    setClassesLoading: (state, action) => {
      state.classesLoading = action.payload;
    },

    // Set classes error
    setClassesError: (state, action) => {
      state.classesError = action.payload;
    },

    // Set selected category
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },

    // Set selected class
    setSelectedClass: (state, action) => {
      state.selectedClass = action.payload;
    },

    // Clear selected class (when navigating back)
    clearSelectedClass: (state) => {
      state.selectedClass = null;
    },

    // Set students list
    setStudents: (state, action) => {
      state.students = action.payload;
    },

    // Set selected student IDs
    setSelectedStudentIds: (state, action) => {
      state.selectedStudentIds = action.payload;
    },

    // Toggle student selection
    toggleStudentSelection: (state, action) => {
      const studentId = action.payload;
      const index = state.selectedStudentIds.indexOf(studentId);
      if (index > -1) {
        state.selectedStudentIds.splice(index, 1);
      } else {
        state.selectedStudentIds.push(studentId);
      }
    },

    // Set enrollment data
    setEnrollmentData: (state, action) => {
      state.enrollmentData = action.payload;
    },

    // Clear enrollment data after payment
    clearEnrollmentData: (state) => {
      state.selectedStudentIds = [];
      state.enrollmentData = null;
    },

    // Set country
    setCountry: (state, action) => {
      state.country = action.payload;
    },

    // Clear categories cache
    clearCategories: (state) => {
      state.categories = {};
      state.selectedCategory = null;
    },

    // Clear classes cache
    clearClasses: (state) => {
      state.classes = {};
      state.selectedClass = null;
    },

    // Clear all class data
    clearAllClassData: (state) => {
      state.selectedCenter = null;
      state.categories = {};
      state.categoriesLoading = false;
      state.categoriesError = null;
      state.classes = {};
      state.classesLoading = false;
      state.classesError = null;
      state.selectedCategory = null;
      state.selectedClass = null;
    },
  },
});

export const {
  setSelectedCenter,
  setCategories,
  setCategoriesLoading,
  setCategoriesError,
  setClasses,
  setClassesLoading,
  setClassesError,
  setSelectedCategory,
  setSelectedClass,
  clearSelectedClass,
  setStudents,
  setSelectedStudentIds,
  toggleStudentSelection,
  setEnrollmentData,
  clearEnrollmentData,
  setCountry,
  clearCategories,
  clearClasses,
  clearAllClassData,
} = classSlice.actions;

export default classSlice.reducer;
